import Button from 'antd/lib/button';
import Card from 'antd/lib/card';
import Col from 'antd/lib/col';
import DatePicker from 'antd/lib/date-picker';
import Divider from 'antd/lib/divider';
import Form, { RuleRender } from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Radio from 'antd/lib/radio';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Switch from 'antd/lib/switch';
import Typography from 'antd/lib/typography';
import { sortBy } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { endOfDay, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { Market, marketMaxPrices } from '../../../../domain/market-prices/interface';
import { PRICE_RESPONSE_MARKETS_DATA, PriceType } from '../../../../domain/price-response/interface';
import { PriceResponseMarket } from './PriceResponseMarket';
import { marketLoadZoneToLabel } from '../../../../components/site/SiteMarketAndLoadZone';
import { getPresets } from '../../../../components/selectors/DatePeriodSelectorWithPresets/DatePeriodSelectorWithPresets';

export interface IScenarioFormValues {
    market: Market;
    zone: string;
    priceType: PriceType;
    dateRange: [Dayjs, Dayjs];
    triggerPrice: number;
    estimatedKw: number;
    eventDurationHours: number;
    stopIfPriceDrops: boolean;
    cooldownHours: number;
    maxDispatchesPerDay?: number;
}

export interface IScenarioSubmitValues {
    market: Market;
    zone: string;
    priceType: PriceType;
    start: Date;
    end: Date;
    triggerPrice: number;
    estimatedKw: number;
    eventDurationHours: number;
    stopIfPriceDrops: boolean;
    cooldownHours: number;
    maxDispatchesPerDay?: number;
}

interface IPriceResponseScenariosFormProps {
    isCalculating: boolean;
    onCalculate: (values: IScenarioSubmitValues) => void;
}

const INITIAL_VALUES: Partial<IScenarioFormValues> = {
    priceType: PriceType.REAL_TIME,
    estimatedKw: 1,
    eventDurationHours: 1,
    stopIfPriceDrops: false,
    cooldownHours: 1,
};

const MAX_DISPATCHES_PER_DAY_LIMIT = 24;

const priceResponseMarketOptions = Object.keys(PRICE_RESPONSE_MARKETS_DATA)
    .sort()
    .map(market => ({
        label: market,
        value: market.toLowerCase(),
    }));

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>
        <Typography.Text strong style={{ fontSize: 15 }}>
            {children}
        </Typography.Text>
    </Divider>
);

export const PriceResponseScenariosForm = ({ isCalculating, onCalculate }: IPriceResponseScenariosFormProps) => {
    const [form] = Form.useForm<IScenarioFormValues>();

    const market = Form.useWatch('market', form);

    const zoneOptions = market
        ? sortBy(
              (PRICE_RESPONSE_MARKETS_DATA[market.toUpperCase()] || []).map(zone => ({ label: marketLoadZoneToLabel(market, zone), value: zone })),
              'label'
          )
        : [];

    const handleMarketChange = () => {
        form.setFieldsValue({ zone: undefined });
        // Re-validate triggerPrice since its max allowed value depends on the selected market
        form.isFieldTouched('triggerPrice') && form.validateFields(['triggerPrice']);
    };

    const disabledDate = (current: Dayjs) => (current ? current.isAfter(dayjs(), 'day') : false);

    const validatePrice: RuleRender = () => ({
        validator(_rule, value) {
            if (value === undefined || value === null) return Promise.resolve();
            // marketMaxPrices is keyed by the Market enum's casing (e.g. 'PJM'), but the form
            // stores the market value lowercased, so it must be uppercased before lookup.
            const max = marketMaxPrices[market?.toUpperCase() as unknown as keyof typeof marketMaxPrices] ?? Infinity;
            if (value > max) {
                return Promise.reject(`The price is too high for this market. ${market?.toUpperCase()} maximum trigger is $${max}`);
            }
            return Promise.resolve();
        },
    });

    const handleDecimalNumberInput =
        (maxDecimals: number = 0) =>
        (event: any) => {
            const charCode = typeof event.which == 'undefined' ? event.keyCode : event.which;
            const charStr = String.fromCharCode(charCode);
            const currentValue: string = event.target.value || '';

            // If no decimals allowed at all, just delegate to the digits-only handler
            if (maxDecimals <= 0) {
                if (!/^\d$/.test(charStr)) event.preventDefault();
                return;
            }

            // Allow a single decimal separator (. or ,)
            if (charStr === '.' || charStr === ',') {
                if (currentValue.includes('.') || currentValue.includes(',')) {
                    event.preventDefault();
                }
                return;
            }

            // Block anything that isn't a digit
            if (!/^\d$/.test(charStr)) {
                event.preventDefault();
                return;
            }

            // Digit typed — block it if there are already maxDecimals digits after the separator
            const decimalMatch = currentValue.match(/[.,](\d*)$/);
            if (decimalMatch && decimalMatch[1].length >= maxDecimals) {
                event.preventDefault();
            }
        };

    const onFinish = (values: IScenarioFormValues) => {
        const prMarket = new PriceResponseMarket(values.market, values.zone);
        const [startDate, endDate] = values.dateRange;

        onCalculate({
            market: values.market,
            zone: values.zone,
            priceType: values.priceType,
            start: fromZonedTime(startOfDay(startDate.toDate()), prMarket.timezone),
            end: fromZonedTime(endOfDay(endDate.toDate()), prMarket.timezone),
            triggerPrice: values.triggerPrice,
            estimatedKw: values.estimatedKw,
            eventDurationHours: values.eventDurationHours,
            stopIfPriceDrops: !!values.stopIfPriceDrops,
            cooldownHours: values.cooldownHours,
            maxDispatchesPerDay: values.maxDispatchesPerDay || undefined,
        });
    };

    return (
        <Card style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" initialValues={INITIAL_VALUES} onFinish={onFinish}>
                <SectionTitle>Market & Time Range</SectionTitle>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="market"
                            label={<Typography.Text strong>Market</Typography.Text>}
                            rules={[{ required: true, message: 'Please select market!' }]}
                        >
                            <Select size="large" placeholder="Select market" options={priceResponseMarketOptions} onChange={handleMarketChange} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="zone"
                            label={<Typography.Text strong>Zone</Typography.Text>}
                            rules={[{ required: true, message: 'Please select zone!' }]}
                        >
                            <Select
                                size="large"
                                placeholder="Select zone"
                                options={zoneOptions}
                                disabled={!market}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="dateRange"
                            label={<Typography.Text strong>Date Range</Typography.Text>}
                            rules={[{ required: true, message: 'Please select a date range!' }]}
                        >
                            <DatePicker.RangePicker
                                size="large"
                                style={{ width: '100%' }}
                                format="DD/MMM/YYYY"
                                disabledDate={disabledDate}
                                presets={getPresets(2024)}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <SectionTitle>Event Trigger Settings</SectionTitle>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="triggerPrice"
                            label={<Typography.Text strong>Trigger Price $/MWh</Typography.Text>}
                            tooltip={{
                                title: `Market $/MWh Maximum\nPJM=$10000\nCAISO=$1000\nERCOT=$5000\nNYISO=$10000\nISONE=$10000`,
                                overlayStyle: { whiteSpace: 'pre-line' },
                            }}
                            rules={[{ required: true, message: 'Please enter trigger price!' }, validatePrice]}
                        >
                            <InputNumber
                                size="large"
                                min={0}
                                style={{ width: '100%' }}
                                placeholder="Trigger price"
                                onKeyPress={handleDecimalNumberInput()}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="priceType"
                            label={<Typography.Text strong>Price Type</Typography.Text>}
                            rules={[{ required: true, message: 'Please select price type!' }]}
                        >
                            <Radio.Group>
                                <Radio value={PriceType.REAL_TIME}>Real Time</Radio>
                                <Radio value={PriceType.DAY_AHEAD}>Day Ahead</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="stopIfPriceDrops"
                            label={<Typography.Text strong>Stop event if price drops below trigger</Typography.Text>}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="estimatedKw"
                            label={<Typography.Text strong>Estimated kW</Typography.Text>}
                            tooltip="Estimated kW value of reduced load during an event"
                            rules={[
                                { required: true, message: 'Please enter estimated kW!' },
                                {
                                    validator: (_, value) =>
                                        value > 0 && value < 100000
                                            ? Promise.resolve()
                                            : Promise.reject(new Error('kW must be greater than 0 and less than 100000')),
                                },
                            ]}
                        >
                            <InputNumber
                                size="large"
                                min={0}
                                style={{ width: '100%' }}
                                placeholder="Estimated kW"
                                onKeyPress={handleDecimalNumberInput()}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="eventDurationHours"
                            label={<Typography.Text strong>Event Duration (hours)</Typography.Text>}
                            tooltip="Duration of the event created when the price goes above the trigger"
                            rules={[
                                { required: true, message: 'Please enter event duration!' },
                                {
                                    validator: (_, value) =>
                                        value > 0 ? Promise.resolve() : Promise.reject(new Error('Duration must be greater than 0')),
                                },
                            ]}
                        >
                            <InputNumber
                                size="large"
                                min={0.25}
                                max={8}
                                step={0.25}
                                style={{ width: '100%' }}
                                placeholder="Event duration"
                                onKeyPress={handleDecimalNumberInput(2)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="cooldownHours"
                            label={<Typography.Text strong>Cooldown (hours)</Typography.Text>}
                            tooltip="Amount of time after an event needed before returning to normal state"
                            rules={[
                                { required: true, message: 'Please enter cooldown hours!' },
                                {
                                    validator: (_, value) =>
                                        value >= 0 && value <= 24 ? Promise.resolve() : Promise.reject(new Error('Cooldown must be in range 0 - 24')),
                                },
                            ]}
                        >
                            <InputNumber
                                size="large"
                                min={0}
                                max={24}
                                step={0.25}
                                style={{ width: '100%' }}
                                placeholder="Cooldown"
                                onKeyPress={handleDecimalNumberInput(2)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                            name="maxDispatchesPerDay"
                            label={<Typography.Text strong>Max Dispatches Per Day</Typography.Text>}
                            tooltip="Max number of events that can be created within one calendar day"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value === undefined || value === null || value <= MAX_DISPATCHES_PER_DAY_LIMIT
                                            ? Promise.resolve()
                                            : Promise.reject(new Error(`Max dispatches per day cannot exceed ${MAX_DISPATCHES_PER_DAY_LIMIT}`)),
                                },
                            ]}
                        >
                            <InputNumber
                                size="large"
                                min={0}
                                max={MAX_DISPATCHES_PER_DAY_LIMIT}
                                step={1}
                                style={{ width: '100%' }}
                                placeholder="Max Dispatches Per Day"
                                onKeyPress={handleDecimalNumberInput()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 8]} align="middle" justify="end" style={{ marginTop: 16 }}>
                    <Col xs={24} sm={24} md={8} lg={12} style={{ textAlign: 'right' }}>
                        <Button type="primary" size="large" htmlType="submit" loading={isCalculating} style={{ marginTop: 4 }}>
                            Calculate
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};
